import { useState, useRef, useLayoutEffect, useEffect } from "react";
import axios from "axios";

// const API_URL = "http://127.0.0.1:8080";
const API_URL = "https://links-131332180612.us-central1.run.app";

export default function useHintSystem(problem, playAudio, setHintActive, setIsClarificationPlaying) {
  const [hint, setHint] = useState("");
  const [clarification, setClarification] = useState("");
  const [hintAudio, setHintAudio] = useState(null);
  const [clarificationAudio, setClarificationAudio] = useState(null);
  const [hintCount, setHintCount] = useState(0);
  const [hintClosing, setHintClosing] = useState(false);
  const [showClarificationButton, setShowClarificationButton] = useState(true);
  const [isHintLoading, setIsHintLoading] = useState(false);
  const [timings, setTimings] = useState(null);
  const hintBoxRef = useRef(null);

  // Check if the API server is reachable (only on initial load)
  // useEffect(() => {
  //   axios.get(`${API_URL}/health`).catch(error => {
  //     console.error("API server is not reachable");
  //   });
  // }, []);

  // Animate hint box height changes
  useLayoutEffect(() => {
    if (hintBoxRef.current && hint) {
      const element = hintBoxRef.current;
      
      // First set a fixed height based on the current displayed content
      element.style.height = element.offsetHeight + "px";
      
      // Force a reflow
      void element.offsetHeight;
      
      // Then immediately set the height to auto to measure the new content size
      element.style.height = "auto";
      const newHeight = element.scrollHeight;
      
      // Set back to the original height
      element.style.height = element.offsetHeight + "px";
      
      // Force another reflow
      void element.offsetHeight;
      
      // Then animate to the new height
      requestAnimationFrame(() => {
        element.style.height = newHeight + "px";
      });
      
      // Remove the fixed height after the transition completes
      const resetHeight = () => {
        if (element) element.style.height = "auto";
      };
      
      element.addEventListener("transitionend", resetHeight, { once: true });
      return () => element.removeEventListener("transitionend", resetHeight);
    }
  }, [hint]);

  const requestHint = async (canvas) => {
    if (!canvas) {
      console.error("No canvas provided to requestHint");
      return;
    }
    
    setHint("Loading hint...");
    setIsHintLoading(true);
    setHintActive(true);
    
    try {
      // Start timing
      const startTime = performance.now();
      
      // Get canvas blob
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      
      // Prepare form data
      const formData = new FormData();
      formData.append("image", blob, "answer.png");
      formData.append("problem", problem.display || problem);
      
      // Make API request
      const response = await axios.post(`${API_URL}/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000 // 30 second timeout
      });
      
      // End timing
      const endTime = performance.now();
      const duration = endTime - startTime;
      setTimings({ requestStart: startTime, requestEnd: endTime, requestDuration: duration });
      
      // Extract data from response
      const { hint: hintText, audio } = response.data;
      
      // Update state with hint text
      setHint(hintText);
      setHintCount(prevCount => prevCount + 1);
      
      // Try playing audio
      if (audio) {
        playAudio(audio);
      } else {
        console.warn("No audio received from API");
        setHintActive(false);
        setIsClarificationPlaying(false);
      }
      
      setIsHintLoading(false);
      
      // Return result so requestHint in MathApp can use it
      return { 
        hintText,
        audioUrl: audio
      };
      
    } catch (error) {
      console.error("Error requesting hint:", error.message);
      
      setHint("Sorry, I couldn't generate a hint right now. Please try again.");
      setHintActive(false);
      setIsClarificationPlaying(false);
      setIsHintLoading(false);
    }
  };

  const requestClarification = async (canvas) => {
    // Immediately disable both buttons
    setIsClarificationPlaying(true);
    setHintActive(true);
    setIsHintLoading(true);
    
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
    const formData = new FormData();
    formData.append("image", blob, "answer.png");
    formData.append("problem", problem.eval || problem);
    formData.append("previous_hint", hint);

    try {
      const response = await axios.post(`${API_URL}/clarify-hint`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setClarification(response.data.clarification);
      setClarificationAudio(response.data.audio);
      setHint(response.data.clarification);
      
      // Don't hide the button, just keep it disabled
      // setShowClarificationButton(false); 
      
      // Play the audio if available
      if (response.data.audio) {
        playAudio(response.data.audio);
      } else {
        setIsClarificationPlaying(false);
        setHintActive(false);
      }
      
      setIsHintLoading(false);
    } catch (error) {
      console.error("Error requesting clarification:", error.message);
      setIsClarificationPlaying(false);
      setHintActive(false);
      setIsHintLoading(false);
    }
  };

  const handleCloseHint = (stopAudio) => {
    stopAudio();
    
    setHintClosing(true);
    setTimeout(() => {
      setHint("");
      setHintClosing(false);
      setShowClarificationButton(true);
    }, 500);
  };

  return {
    hint,
    setHint,
    hintCount,
    setHintCount,
    timings,
    hintBoxRef,
    hintClosing,
    showClarificationButton,
    isHintLoading,
    requestHint,
    requestClarification,
    handleCloseHint
  };
}
